import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Link2,
  Plus,
  RefreshCw,
  Trash2,
  Users,
  Loader2,
  AlertCircle,
  Instagram,
  Facebook,
  Music2,
  Clock,
  Youtube,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { parseUTCDate } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";

const formatCount = (n) => {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
};

const platformMeta = (platform) => {
  switch (platform) {
    case "instagram":
      return {
        label: "Instagram",
        Icon: Instagram,
        color: "bg-purple-500/15 text-purple-500 border-purple-500/20",
      };
    case "facebook":
      return {
        label: "Facebook",
        Icon: Facebook,
        color: "bg-blue-500/15 text-blue-500 border-blue-500/20",
      };
    case "tiktok":
      return {
        label: "TikTok",
        Icon: Music2,
        color: "bg-pink-500/15 text-pink-500 border-pink-500/20",
      };
    case "youtube":
      return {
        label: "YouTube",
        Icon: Youtube,
        color: "bg-red-500/15 text-red-500 border-red-500/20",
      };
    default:
      return {
        label: platform,
        Icon: Link2,
        color: "bg-gray-500/15 text-gray-500 border-gray-500/20",
      };
  }
};

const AccountsTab = ({
  isDark,
  accounts,
  onRefresh,
  connectMeta,
  connectTikTok,
  connectYouTube,
  syncAccount,
  deleteAccount,
  loadingAccounts,
}) => {
  const [syncing, setSyncing] = useState(null);
  const [connecting, setConnecting] = useState(null); // null | 'meta' | 'youtube'
  const [pendingDelete, setPendingDelete] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [tiktokNotice, setTiktokNotice] = useState(null);

  const [oauthBanner, setOauthBanner] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const metaStatus = params.get("meta_status");
    const ytStatus = params.get("yt_status");
    const ttStatus = params.get("tt_status");
    const status = metaStatus || ytStatus || ttStatus;
    if (!status) return;
    if (status === "connected") {
      setOauthBanner({ type: "success", message: "Account succesvol gekoppeld!" });
      onRefresh?.();
    } else if (status === "denied") {
      setOauthBanner({ type: "error", message: "Koppeling geweigerd." });
    } else {
      setOauthBanner({
        type: "error",
        message: params.get("msg") || params.get("message") || `Koppelen mislukt (${status}).`,
      });
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("meta_status");
    url.searchParams.delete("yt_status");
    url.searchParams.delete("tt_status");
    url.searchParams.delete("accounts");
    url.searchParams.delete("message");
    url.searchParams.delete("msg");
    window.history.replaceState({}, "", url.toString());
  }, [onRefresh]);

  const handleConnectMeta = useCallback(async () => {
    setConnecting("meta");
    setActionError(null);
    try {
      const { url } = await connectMeta();
      if (url) window.location.href = url;
    } catch (e) {
      setActionError(e?.response?.data?.message || "Koppelen met Meta mislukt.");
      setConnecting(null);
    }
  }, [connectMeta]);

  const handleConnectYouTube = useCallback(async () => {
    setConnecting("youtube");
    setActionError(null);
    try {
      const { url } = await connectYouTube();
      if (url) window.location.href = url;
    } catch (e) {
      setActionError(e?.response?.data?.message || "Koppelen met YouTube mislukt.");
      setConnecting(null);
    }
  }, [connectYouTube]);

  const handleConnectTikTok = useCallback(async () => {
    setConnecting("tiktok");
    setActionError(null);
    setTiktokNotice(null);
    try {
      const { url } = await connectTikTok();
      if (url) window.location.href = url;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "";
      if (e?.response?.data?.code === "TIKTOK_NOT_CONFIGURED") {
        setTiktokNotice(msg);
      } else {
        setActionError(msg || "Koppelen met TikTok mislukt.");
      }
      setConnecting(null);
    }
  }, [connectTikTok]);

  const handleSync = useCallback(
    async (id) => {
      setSyncing(id);
      setActionError(null);
      try {
        await syncAccount(id);
        await onRefresh?.();
      } catch (e) {
        setActionError(e?.response?.data?.message || "Sync mislukt.");
      } finally {
        setSyncing(null);
      }
    },
    [syncAccount, onRefresh]
  );

  const handleDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await deleteAccount(pendingDelete);
      setPendingDelete(null);
      await onRefresh?.();
    } catch (e) {
      setActionError(e?.response?.data?.message || "Verwijderen mislukt.");
      setPendingDelete(null);
    }
  }, [pendingDelete, deleteAccount, onRefresh]);

  return (
    <div className="space-y-4">
      {/* ===== Header / Connect buttons ===== */}
      <div
        className={cn(
          "rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3",
          isDark
            ? "bg-white/[0.02] border-white/[0.06]"
            : "bg-white border-gray-200/70"
        )}
      >
        <div>
          <h2
            className={cn(
              "text-[14px] font-semibold",
              isDark ? "text-white" : "text-gray-900"
            )}
          >
            Gekoppelde accounts
          </h2>
          <p
            className={cn(
              "text-[12px] mt-0.5",
              isDark ? "text-gray-500" : "text-gray-500"
            )}
          >
            Synchroniseer je content automatisch met views, likes, comments en volgers.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={handleConnectMeta}
            disabled={!!connecting}
            className="gap-1.5"
          >
            {connecting === "meta" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Instagram / Facebook
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleConnectYouTube}
            disabled={!!connecting}
            className="gap-1.5 text-red-500 border-red-500/30 hover:bg-red-500/10"
          >
            {connecting === "youtube" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Youtube className="h-3.5 w-3.5" />
            )}
            YouTube
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleConnectTikTok}
            disabled={!!connecting}
            className="gap-1.5"
          >
            {connecting === "tiktok" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Music2 className="h-3.5 w-3.5" />
            )}
            TikTok
          </Button>
        </div>
      </div>

      {oauthBanner && (
        <div
          className={cn(
            "rounded-lg border p-3 text-[13px] flex items-start gap-2",
            oauthBanner.type === "success"
              ? isDark
                ? "bg-emerald-500/[0.08] border-emerald-500/20 text-emerald-300"
                : "bg-emerald-50 border-emerald-200 text-emerald-700"
              : isDark
                ? "bg-red-500/[0.08] border-red-500/20 text-red-300"
                : "bg-red-50 border-red-200 text-red-700"
          )}
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{oauthBanner.message}</span>
        </div>
      )}

      {tiktokNotice && (
        <div
          className={cn(
            "rounded-lg border p-3 text-[13px] flex items-start gap-2",
            isDark
              ? "bg-yellow-500/[0.08] border-yellow-500/20 text-yellow-300"
              : "bg-yellow-50 border-yellow-200 text-yellow-700"
          )}
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{tiktokNotice}</span>
        </div>
      )}

      {actionError && (
        <div
          className={cn(
            "rounded-lg border p-3 text-[13px] flex items-start gap-2",
            isDark
              ? "bg-red-500/[0.08] border-red-500/20 text-red-300"
              : "bg-red-50 border-red-200 text-red-700"
          )}
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{actionError}</span>
        </div>
      )}

      {/* ===== Accounts list ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {loadingAccounts && accounts.length === 0 && (
          <div className="col-span-full flex items-center justify-center py-12">
            <Loader2
              className={cn(
                "h-6 w-6 animate-spin",
                isDark ? "text-gray-500" : "text-gray-400"
              )}
            />
          </div>
        )}

        {!loadingAccounts && accounts.length === 0 && (
          <div
            className={cn(
              "col-span-full text-center py-12 rounded-xl border-2 border-dashed",
              isDark
                ? "border-white/[0.06] text-gray-500"
                : "border-gray-200 text-gray-500"
            )}
          >
            <Link2 className="h-7 w-7 mx-auto mb-2 opacity-50" />
            <p className="text-[13px] font-medium">Nog geen accounts gekoppeld</p>
            <p className="text-[12px] mt-0.5">
              Koppel je Instagram of Facebook om automatisch te synchroniseren.
            </p>
          </div>
        )}

        {accounts.map((a) => {
          const meta = platformMeta(a.platform);
          const Icon = meta.Icon;
          const lastSync = a.last_synced_at
            ? parseUTCDate(a.last_synced_at)
            : null;
          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                "rounded-xl border p-4",
                isDark
                  ? "bg-white/[0.02] border-white/[0.06]"
                  : "bg-white border-gray-200/70"
              )}
            >
              <div className="flex items-start gap-3">
                {a.profile_picture_url ? (
                  <img
                    src={a.profile_picture_url}
                    alt={a.account_name || "account"}
                    className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    className={cn(
                      "h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0",
                      isDark ? "bg-white/[0.06]" : "bg-gray-100"
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-5 w-5",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}
                    />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p
                      className={cn(
                        "text-[13px] font-semibold truncate",
                        isDark ? "text-white" : "text-gray-900"
                      )}
                    >
                      {a.account_name || a.account_username || "Onbekend"}
                    </p>
                    <span
                      className={cn(
                        "px-1.5 py-0.5 rounded text-[10px] font-semibold border",
                        meta.color
                      )}
                    >
                      {meta.label}
                    </span>
                  </div>
                  {a.account_username && a.account_username !== a.account_name && (
                    <p
                      className={cn(
                        "text-[11px] mt-0.5",
                        isDark ? "text-gray-500" : "text-gray-500"
                      )}
                    >
                      @{a.account_username}
                    </p>
                  )}

                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <div className="flex items-center gap-1">
                      <Users
                        className={cn(
                          "h-3 w-3",
                          isDark ? "text-gray-500" : "text-gray-400"
                        )}
                      />
                      <span
                        className={cn(
                          "text-[12px] font-medium",
                          isDark ? "text-gray-300" : "text-gray-700"
                        )}
                      >
                        {formatCount(a.followers_count)} volgers
                      </span>
                    </div>
                    {a.media_count > 0 && (
                      <Badge
                        variant="outline"
                        className="h-[18px] text-[10px] px-1.5"
                      >
                        {formatCount(a.media_count)} posts
                      </Badge>
                    )}
                  </div>

                  {lastSync && (
                    <div className="flex items-center gap-1 mt-2">
                      <Clock
                        className={cn(
                          "h-3 w-3",
                          isDark ? "text-gray-600" : "text-gray-400"
                        )}
                      />
                      <span
                        className={cn(
                          "text-[11px]",
                          isDark ? "text-gray-500" : "text-gray-500"
                        )}
                      >
                        Laatste sync: {lastSync.toLocaleString("nl-BE")}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSync(a.id)}
                    disabled={syncing === a.id}
                    className="h-7 gap-1 text-[11px] px-2"
                  >
                    {syncing === a.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    Sync
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingDelete(a.id)}
                    className={cn(
                      "h-7 gap-1 text-[11px] px-2",
                      isDark
                        ? "text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        : "text-red-600 hover:text-red-700 hover:bg-red-50"
                    )}
                  >
                    <Trash2 className="h-3 w-3" />
                    Ontkoppel
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account ontkoppelen?</AlertDialogTitle>
            <AlertDialogDescription>
              De gesynchroniseerde video's blijven behouden, maar er wordt niet meer
              automatisch geüpdatet.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Ontkoppelen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AccountsTab;
